import React from "react";
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Avatar,
  Typography,
  Chip,
  Stack,
  Button,
  IconButton,
  Tooltip,
  alpha,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineTwoToneIcon from "@mui/icons-material/DeleteOutlineTwoTone";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";

const ACCENT = "#1976d2";

const TRADE_DELAY_LABELS = {
  headManager: "מנהל ראשי",
  manager: "מנהל",
  employee: "מתאמת סחר",
  supplier: "ספק חיצוני",
};

const getInitials = (user) => `${user.Name?.[0] || ""}${user.LastName?.[0] || ""}`.toUpperCase() || "?";

const UserCard = ({ user, rolesList, onEdit, onDelete }) => {
  const roleNames =
    user?.Role && user.Role.length > 0
      ? user.Role.map((roleId) => rolesList.find((r) => r?._id === roleId)?.Name).filter(Boolean)
      : [];

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${alpha("#000000", 0.06)}`,
        background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)",
          transform: "translateY(-4px)",
          borderColor: alpha(ACCENT, 0.4),
        },
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha("#f8fafc", 0.8)} 0%, ${alpha(ACCENT, 0.08)} 100%)`,
          p: 2.5,
          borderBottom: `2px solid ${alpha(ACCENT, 0.12)}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
          <Avatar
            sx={{
              background: `linear-gradient(135deg, ${ACCENT} 0%, ${alpha(ACCENT, 0.8)} 100%)`,
              width: 48,
              height: 48,
              fontSize: "1.2rem",
              fontWeight: 700,
              boxShadow: `0 4px 12px ${alpha(ACCENT, 0.4)}`,
            }}
          >
            {getInitials(user)}
          </Avatar>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: "1.05rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            title={`${user?.Name} ${user?.LastName}`}
          >
            {user?.Name} {user?.LastName}
          </Typography>
        </Box>
        <Tooltip title="מחיקת משתמש">
          <IconButton size="small" color="warning" onClick={() => onDelete(user?._id)} aria-label="delete user">
            <DeleteOutlineTwoToneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EmailIcon sx={{ color: "#64748b", fontSize: "1.1rem" }} />
          <Typography variant="body2" color="text.secondary" noWrap title={user?.Email}>
            {user?.Email?.toLowerCase()}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BusinessIcon sx={{ color: "#64748b", fontSize: "1.1rem" }} />
          <Typography variant="body2" color="text.secondary">
            {user?.Branches && user.Branches.length > 0 ? `${user.Branches.length} סניפים` : "כל הסניפים"}
          </Typography>
        </Box>

        {roleNames.length > 0 && (
          <Stack direction="row" gap={0.75} flexWrap="wrap">
            {roleNames.map((name) => (
              <Chip key={name} label={name} size="small" sx={{ bgcolor: alpha(ACCENT, 0.08), color: ACCENT, fontWeight: 600 }} />
            ))}
          </Stack>
        )}

        {user?.tradeDelayRole && (
          <Chip
            label={TRADE_DELAY_LABELS[user.tradeDelayRole] || user.tradeDelayRole}
            size="small"
            variant="outlined"
            sx={{ alignSelf: "flex-start" }}
          />
        )}
      </CardContent>

      <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
        <Button
          fullWidth
          variant="contained"
          size="small"
          startIcon={<EditOutlinedIcon />}
          onClick={() => onEdit(user)}
          sx={{
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 600,
            background: `linear-gradient(135deg, ${ACCENT} 0%, ${alpha(ACCENT, 0.85)} 100%)`,
          }}
        >
          עריכה
        </Button>
      </CardActions>
    </Card>
  );
};

export default UserCard;
